import { ChangeDetectionStrategy, Component, computed, input, type Type } from '@angular/core';

import { ChatMessage, ChatPart } from '@core/tellent-assistant-types';
import { classifyAssistantMessage } from '@core/tellent-assistant-util';
import { IconButtonComponent } from '@core/user-interface-legacy';

import { TellentAssistantBrandMarkComponent } from '../brand-mark/tellent-assistant-brand-mark.component';
import {
  type DisclosureStep,
  TellentAssistantStepsDisclosureComponent,
} from '../parts/steps-disclosure/steps-disclosure.component';
import { TellentAssistantTextPartComponent } from '../parts/text-part/text-part.component';
import { TellentAssistantToolCallPartComponent } from '../parts/tool-call-part/tool-call-part.component';
import { TellentAssistantToolPillComponent } from '../parts/tool-pill/tool-pill.component';

export type RenderComponentResolver = (
  toolName: string,
  args: unknown,
) => Type<unknown> | undefined;

@Component({
  selector: 'tellent-assistant-message',
  standalone: true,
  imports: [
    IconButtonComponent,
    TellentAssistantBrandMarkComponent,
    TellentAssistantTextPartComponent,
    TellentAssistantToolCallPartComponent,
    TellentAssistantToolPillComponent,
    TellentAssistantStepsDisclosureComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <article [class]="'role-' + message().role">
      @if (message().role === 'assistant') {
        <tellent-assistant-brand-mark class="avatar" />
      }
      <div class="body">
        @if (isAssistant()) {
          @if (preamble(); as preambleText) {
            <tellent-assistant-text-part
              [content]="preambleText.content"
              [done]="preambleText.done"
            />
          }
          @if (runningStep(); as runningToolName) {
            <tellent-assistant-tool-pill [toolName]="runningToolName" />
          } @else if (completedSteps().length > 0) {
            <tellent-assistant-steps-disclosure [steps]="completedSteps()" />
          }
          @for (renderTool of renderTools(); track renderTool.toolCallId) {
            <tellent-assistant-tool-call-part
              [name]="renderTool.name"
              [toolCallId]="renderTool.toolCallId"
              [args]="renderTool.args"
              [status]="renderTool.status"
              [durationMs]="renderTool.durationMs ?? null"
              [renderComponent]="resolveRenderComponent()(renderTool.name, renderTool.args)"
            />
          }
          @if (finalText(); as finalTextPart) {
            <tellent-assistant-text-part
              [content]="finalTextPart.content"
              [done]="finalTextPart.done"
            />
          }
          <rt-icon-button
            iconName="content-copy"
            size="small"
            variant="plain"
            class="copy"
            (click)="copy()"
          />
        } @else {
          @for (part of message().parts; track partKey(part)) {
            @switch (part.kind) {
              @case ('text') {
                <tellent-assistant-text-part [content]="part.content" [done]="part.done" />
              }
            }
          }
        }
      </div>
    </article>
  `,
  styleUrl: './tellent-assistant-message.component.less',
})
export class TellentAssistantMessageComponent {
  protected readonly isAssistant = computed(() => this.message().role === 'assistant');

  protected readonly phases = computed(() =>
    classifyAssistantMessage(this.message(), this.renderToolNames()),
  );

  protected readonly preamble = computed(() => this.phases().preamble);
  protected readonly finalText = computed(() => this.phases().finalText);
  protected readonly renderTools = computed(() => this.phases().renderTools);

  protected readonly runningStep = computed<string | null>(() => {
    const tools = this.phases().dataTools;
    const last = tools[tools.length - 1];
    if (!last) return null;
    return last.hasResult ? null : last.name;
  });

  protected readonly completedSteps = computed<readonly DisclosureStep[]>(() => {
    if (this.runningStep() !== null) return [];
    return this.phases().dataTools.map(t => ({
      toolCallId: t.toolCallId,
      name: t.name,
      status: t.status,
    }));
  });

  public readonly message = input.required<ChatMessage>();
  public readonly resolveRenderComponent = input<RenderComponentResolver>(() => undefined);
  public readonly renderToolNames = input<ReadonlySet<string>>(new Set<string>());

  protected partKey(part: ChatPart): string {
    switch (part.kind) {
      case 'text':
        return `text-${part.messageId}`;
      case 'tool-call':
      case 'tool-result':
        return `${part.kind}-${part.toolCallId}`;
      default:
        return part.kind;
    }
  }

  protected async copy(): Promise<void> {
    const text = this.message()
      .parts.filter(p => p.kind === 'text')
      .map(p => (<Extract<ChatPart, { kind: 'text' }>>p).content)
      .join('\n');
    await navigator.clipboard.writeText(text);
  }
}
